import React, { useCallback, useMemo, VoidFunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import {
  HeaderConfig,
  ManageEntitiesContentLayout
} from 'record-it-components/pageContentLayout/ManageEntitiesContentLayout/ManageEntitiesContentLayout';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { UserGroup } from '../../../classes/entities/UserGroup/userGroupTypes';
import { useEmptyCreateEntityButtonConfig } from '../../../hooks/useEmptyCreateEntityButtonConfig';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { useEntityRepository } from '../../../hooks/entity/useEntityRepository';
import { UserGroupList } from '../../userGroup/UserGroupList/UserGroupList';
import { useConfigureBreadcrumbsWithBuilder } from '../../../hooks/misc/useConfigureBreadcrumbsWithBuilder';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';

export type UserGroupAdminPageProps = PropsWithoutChildren<
  Record<never, never>
>;

export const UserGroupAdminPage: VoidFunctionComponent<UserGroupAdminPageProps> =
  React.memo((props) => {
    prepareProps({ props, ignoreChildren: true });

    useConfigureBreadcrumbsWithBuilder((builder) =>
      builder.addHomePage().addUserGroupAdminPage()
    );

    const { t } = useTranslation();
    const userGroups = useEntities(EntityName.UserGroup, 'getAll');
    const userGroupRepository = useEntityRepository(EntityName.UserGroup);

    const handleCreateEntity = useEventCallback(() => {
      userGroupRepository.create({});
    });

    const createEntityButtonConfig = useEmptyCreateEntityButtonConfig({
      onCreateEntity: handleCreateEntity,
      text: t('components.page.UserGroupAdminPage.createEntity')
    });

    const headerConfig: HeaderConfig = useMemo(() => {
      return {
        iconConfig:
          iconConfigurations.prefixed.page.UserGroupAdminPage.titleIcon,
        title: t('components.page.UserGroupAdminPage.pageTitle')
      };
    }, [t]);

    const renderEntities = useCallback(
      (currentPageUserGroups: Array<UserGroup>) => {
        return (
          <UserGroupList userGroups={currentPageUserGroups}></UserGroupList>
        );
      },
      []
    );

    return (
      <ManageEntitiesContentLayout<UserGroup, never>
        createEntityButtonConfig={createEntityButtonConfig}
        headerConfig={headerConfig}
        entities={userGroups}
        renderEntities={renderEntities}
      ></ManageEntitiesContentLayout>
    );
  });
